import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import {
  BREAKPOINTS_WIDTHS,
  useWindowSize,
  PATHS,
  useModal,
} from '@belong/common';
import { Button, Modal, Spinner, Text } from '@belong/ui';
import { getUnitsFromProperties } from 'accounts/utils/units';
import type { MaintenanceRecurrenceModel } from 'api/models';
import { useNotes } from 'common/hooks/useNotes';
import Field from 'components/Field/Field';
import Form from 'components/Form/Form';
import { InputFinalFormAdapter } from 'components/Input/Input';
import {
  SELECTOR_TYPES,
  SelectorFinalFormAdapter,
} from 'components/Selector/Selector';
import FormLayout from 'layouts/FormLayout/FormLayout';
import { RecurringProServiceCard } from 'maintenance/components/recurring-pro-service-card/recurring-pro-service-card';
import { RecurringProServiceStatusTag } from 'maintenance/components/recurring-pro-service-status-tag/recurring-pro-service-status-tag';
import { URL_QUERY_PARAMS } from 'maintenance/constants';
import { STRINGS } from 'maintenance/strings';
import type {
  RecurringProServiceFormSchema,
  RecurringProServicePreset,
} from 'maintenance/types';
import {
  getRecurringTasksByUnit,
  recurringTasksHomeowner,
  residentTasks,
} from 'maintenance/utils';
import HomeownerAccountProperty from 'models/homeownerAccounts/HomeownerAccountProperty';
import { fetchRecurringTasks } from 'store/redux/maintenance/actions';
import { selectRecurringTasks } from 'store/redux/maintenance/selectors';
import {
  cancelRecurringProService,
  createRecurringProService,
  updateRecurringProService,
} from 'store/redux/pros/actions';

type Props = {
  isHomeOwnerView: boolean;
  preset: RecurringProServicePreset;
  properties: HomeownerAccountProperty[];
  // Structural rather than `UnitBasicInfoModel`: the caller passes
  // `currentLease.unitInfo`, an instance of the `UnitBasicInfo` class, not the
  // generated interface. `unitId` is the only field this form reads.
  unit?: { unitId?: string };
};

type NotesType = {
  description?: string;
};

export function RecurringProServiceForm({
  isHomeOwnerView,
  preset,
  properties,
  unit,
}: Props) {
  const { search } = useLocation();
  const externalUnitId = new URLSearchParams(search).get(
    URL_QUERY_PARAMS.UNIT_ID,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [selectedHome, setSelectedHome] = useState(externalUnitId);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isCancelModalOpen, openCancelModal, closeCancelModal] = useModal();
  const dispatch = useDispatch();
  const { push } = useHistory();
  const recurringTasks = useSelector<unknown, MaintenanceRecurrenceModel[]>(
    selectRecurringTasks,
  );
  const { width } = useWindowSize();
  const units = getUnitsFromProperties(properties);

  const recurringTasksByUnit = getRecurringTasksByUnit(recurringTasks);

  // The unit id is optional at every lookup below — `selectedHome` starts as
  // `null`, `home` comes off form state, and `unitId` is optional on the model.
  // Indexing with one produced the literal `"null"` / `"undefined"` key, which
  // this map never holds, so guarding is exactly equivalent.
  const recurringTasksFor = (unitId?: string | null) =>
    unitId ? recurringTasksByUnit[unitId] : undefined;

  const selectedTask = recurringTasksFor(selectedHome)?.find(
    (recurringTask) => recurringTask.recurringTask === preset.recurringTask,
  );
  const recurringId = useMemo(
    () => (selectedTask ? [selectedTask.id] : null),
    [selectedTask],
  );
  const { notes } = useNotes({
    sourceIds: recurringId,
    sourceType: 'MaintenanceRecurrence',
  });

  const isMobile = width <= BREAKPOINTS_WIDTHS.MD;

  const isActiveByLease =
    // `recurringTask` is optional on `preset`; `includes(undefined)` is `false`
    // at runtime, so asserting keeps the same result.
    ['PoolMaintenance', 'Landscaping'].includes(preset.recurringTask!) &&
    (externalUnitId === selectedHome || selectedHome);

  const recurringTaskImages = isHomeOwnerView
    ? recurringTasksHomeowner
    : residentTasks;
  const currentRecurringTask = Object.values(recurringTaskImages)
    .flat()
    .find((homeOwnerTask) => homeOwnerTask.key === preset.recurringTask);

  const availableUnitsForCurrentRecurringTask = units.filter((unitItem) => {
    const unitRecurringTasks = recurringTasksFor(unitItem.basicInfo?.unitId);

    const currentRecurringTaskForUnit = unitRecurringTasks?.find(
      (unitRecurringTask) =>
        unitRecurringTask.recurringTask === preset.recurringTask,
    );

    const isCurrentRecurringTaskActive =
      typeof currentRecurringTaskForUnit !== 'undefined'
        ? !currentRecurringTaskForUnit.isCancelled &&
          !['Cancelled', 'Completed', 'TurnedOff'].includes(
            currentRecurringTaskForUnit.recurringServiceStatus!,
          )
        : false;

    // `property.basicInfo`, `basicInfo` and `property.address` are optional on
    // the generated models but always present on a full-management home, and
    // every read here already dereferenced them unconditionally.
    if (
      isCurrentRecurringTaskActive &&
      unitItem.property.basicInfo!.propertyType === 'MultiUnit'
    ) {
      return true;
    }

    return (
      currentRecurringTask?.multiUnit ||
      unitItem.property.basicInfo!.propertyType === 'SingleFamily'
    );
  });

  const homes = availableUnitsForCurrentRecurringTask
    .map((unitItem) => {
      const formattedAddress = unitItem.basicInfo!.unitNumber
        ? `${unitItem.property.address!.streetAddress}, ${STRINGS.unit} ${unitItem.basicInfo!.unitNumber}`
        : unitItem.property.address!.streetAddress;

      const unitRecurringTasks = recurringTasksFor(unitItem.basicInfo!.unitId);

      const currentRecurringTaskForUnit = unitRecurringTasks?.find(
        (unitRecurringTask) =>
          unitRecurringTask.recurringTask === preset.recurringTask,
      );

      const isCurrentRecurringTaskActive =
        typeof currentRecurringTaskForUnit !== 'undefined'
          ? !currentRecurringTaskForUnit.isCancelled &&
            !['Cancelled', 'Completed', 'TurnedOff'].includes(
              currentRecurringTaskForUnit.recurringServiceStatus!,
            )
          : false;

      return {
        enabled: isCurrentRecurringTaskActive,
        key: unitItem.basicInfo!.unitId,
        value: formattedAddress,
        streetAddress: unitItem.property.address!.streetAddress,
        content: (
          <div className="flex items-center justify-start py-xs pl-xs pr-lg">
            <div className="mr-sm">
              <RecurringProServiceStatusTag
                status={isCurrentRecurringTaskActive ? 'on' : 'off'}
              />
            </div>
            <Text as="span">{formattedAddress}</Text>
          </div>
        ),
      };
    })
    .sort(
      (firstHome, secondHome) =>
        Number(firstHome.enabled) - Number(secondHome.enabled),
    )
    .sort((firstHome, secondHome) => {
      if (firstHome.enabled !== secondHome.enabled) {
        return 0;
      }

      return firstHome.streetAddress!.localeCompare(
        secondHome.streetAddress!,
        undefined,
        {
          numeric: true,
          sensitivity: 'base',
        },
      );
    });

  const getInitialValues = () => {
    const firstAvailableHome = homes.find((home) => !home.enabled);
    let home = null;

    if (isHomeOwnerView) {
      if (externalUnitId) {
        home = externalUnitId;
      } else if (firstAvailableHome?.key) {
        home = firstAvailableHome?.key;
      } else if (selectedHome) {
        home = selectedHome;
      } else if (homes.length) {
        home = homes[0]?.key;
      }
    } else {
      home = unit?.unitId;
    }

    const homeActiveTask = recurringTasksFor(home)?.find(
      (recurringTask) =>
        recurringTask.recurringTask === preset.recurringTask &&
        !['Cancelled', 'Completed', 'TurnedOff'].includes(
          recurringTask.recurringServiceStatus!,
        ),
    );

    const isRecurringServiceActive = isHomeOwnerView
      ? homes.find((homeItem) => homeItem.key === home)?.enabled
      : !!homeActiveTask;

    let frequency: number | undefined;
    if (home && recurringTasksFor(home) && isRecurringServiceActive) {
      frequency = homeActiveTask?.repeatFrequencyDays;
    }

    const notesObj: NotesType = {};
    if (notes?.length) {
      notesObj.description = notes[0].notes[0].note?.body;
    }

    return {
      home,
      frequency,
      ...notesObj,
    };
  };

  async function handleCancel(home: string) {
    try {
      setIsCancelling(true);
      await dispatch(
        cancelRecurringProService({
          recurringTask: preset.recurringTask,
          unitId: home,
        }),
      );
      setIsCancelling(false);

      const urlSearchParams = new URLSearchParams();

      urlSearchParams.append(URL_QUERY_PARAMS.SUCCESS, 'true');
      urlSearchParams.append(URL_QUERY_PARAMS.TURN_OFF, 'true');
      urlSearchParams.append(URL_QUERY_PARAMS.TOAST_VISIBLE, 'true');

      push({
        pathname: isHomeOwnerView
          ? PATHS.HOMEOWNER_ACCOUNT_MAINTENANCE
          : PATHS.RESIDENTS_ACCOUNT_MAINTENANCE,
        search: urlSearchParams.toString(),
      });
    } catch (error) {
      setIsCancelling(false);
      console.error(error);
    }
  }

  async function handleFormSubmit(values: RecurringProServiceFormSchema) {
    try {
      const { description, frequency, home } = values;

      setIsSubmitting(true);

      const urlSearchParams = new URLSearchParams();

      urlSearchParams.append(URL_QUERY_PARAMS.TOAST_VISIBLE, 'true');
      urlSearchParams.append(URL_QUERY_PARAMS.SUCCESS, 'true');

      const initialRecurringTask = recurringTasksFor(home)?.find(
        (recurringTask) =>
          recurringTask.recurringTask === preset.recurringTask &&
          !['Cancelled', 'Completed', 'TurnedOff'].includes(
            recurringTask.recurringServiceStatus!,
          ),
      );

      const initialNoteId = notes?.length
        ? notes[0].notes[0].note?.id
        : undefined;

      if (initialRecurringTask && !initialRecurringTask.isCancelled) {
        await dispatch(
          updateRecurringProService({
            unitId: home,
            recurringTask: preset.recurringTask,
            frequency,
            description,
            noteId: initialNoteId,
          }),
        );

        urlSearchParams.append(URL_QUERY_PARAMS.UPDATE_FREQUENCY, 'true');
      } else {
        await dispatch(
          createRecurringProService({
            homeUniqueId: home,
            recurringTask: preset.recurringTask,
            frequency,
            sourceType: 'Web',
            description,
          }),
        );

        urlSearchParams.append(URL_QUERY_PARAMS.CREATION, 'true');
      }

      push({
        pathname: isHomeOwnerView
          ? PATHS.HOMEOWNER_ACCOUNT_MAINTENANCE
          : PATHS.RESIDENTS_ACCOUNT_MAINTENANCE,
        search: urlSearchParams.toString(),
      });
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  }

  useEffect(() => {
    async function fetchRecurring() {
      setIsLoading(true);

      await dispatch(fetchRecurringTasks());

      setIsLoading(false);
    }

    fetchRecurring();
  }, [dispatch]);

  useEffect(() => {
    if (homes && !selectedHome && !isLoading) {
      if (isHomeOwnerView && externalUnitId) {
        setSelectedHome(externalUnitId);
      } else {
        const firstHome = homes.length ? homes[0] : null;
        const home = isHomeOwnerView ? firstHome?.key || null : unit?.unitId;

        if (home) {
          setSelectedHome(home);
        }
      }
    }
  }, [
    homes,
    selectedHome,
    isLoading,
    externalUnitId,
    isHomeOwnerView,
    unit?.unitId,
  ]);

  const getActiveLeaseUnitRecurringTaskStatus = () => {
    if (!isHomeOwnerView) {
      const leaseUnitRecurringTasks = recurringTasksFor(unit?.unitId);

      const currentRecurringTaskForLeaseUnit = leaseUnitRecurringTasks?.find(
        (unitRecurringTask) =>
          unitRecurringTask.recurringTask === preset.recurringTask,
      );

      const isCurrentRecurringTaskActive =
        typeof currentRecurringTaskForLeaseUnit !== 'undefined'
          ? !currentRecurringTaskForLeaseUnit.isCancelled &&
            !['Cancelled', 'Completed', 'TurnedOff'].includes(
              currentRecurringTaskForLeaseUnit.recurringServiceStatus!,
            )
          : false;

      return isCurrentRecurringTaskActive ? 'on' : 'off';
    }

    return 'off';
  };

  if (isLoading || isSubmitting) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <FormLayout>
      {isSubmitting && <Spinner />}
      <Form
        onSubmit={handleFormSubmit}
        initialValues={getInitialValues()}
        getFormBottomBar={({ form }: any, nextButtonProps: any) => {
          const { home, frequency } = form.getState().values;

          const initialRecurringTaskByHome = recurringTasksFor(home)?.find(
            (recurringTask) =>
              recurringTask.recurringTask === preset.recurringTask &&
              !['Cancelled', 'Completed', 'TurnedOff'].includes(
                recurringTask.recurringServiceStatus!,
              ) &&
              !recurringTask.isCancelled,
          );

          const initialFrequency =
            initialRecurringTaskByHome?.repeatFrequencyDays;
          const isFrequencyPristine = initialFrequency === frequency;
          const isCurrentUserMaintenanceRequester = isHomeOwnerView
            ? initialRecurringTaskByHome?.requestedBy === 'Homeowner'
            : initialRecurringTaskByHome?.requestedBy === 'Resident';

          const isCancelServiceAvailable =
            initialRecurringTaskByHome &&
            !initialRecurringTaskByHome.isCancelled &&
            isCurrentUserMaintenanceRequester;

          return (
            <div className="mb-3xl flex flex-col items-center gap-xl">
              <FormLayout.Button
                buttonClassNames="w-button-fluid sm:w-button-default h-button"
                ctaProps={{
                  label: 'Save',
                  disabled: isFrequencyPristine,
                }}
                nextButtonWrapperProps={nextButtonProps}
              />

              {isCancelServiceAvailable && (
                <Button
                  variant="text"
                  buttonType="danger"
                  onClick={openCancelModal}
                >
                  CANCEL RECURRING SERVICE
                </Button>
              )}
              <Modal
                isOpen={isCancelModalOpen}
                name="cancel_recurring_service"
                onDismiss={closeCancelModal}
                size={isMobile ? 'fullScreen' : 'auto'}
                overlayClassName="z-fullscreen"
                contentClassName="sm:w-[730px] md:w-[730px]"
              >
                <div className="flex flex-col items-center px-2sm pt-3xl sm:px-lg sm:pt-2xl">
                  {isCancelling && <Spinner />}
                  <Text
                    variant="h2"
                    fontWeight="semibold"
                    className="text-center"
                  >
                    Are you sure you want to cancel the recurring work?
                  </Text>
                  <Button
                    variant="solid"
                    buttonType="primary"
                    className="mb-lg mt-xl"
                    onClick={() => handleCancel(home)}
                  >
                    Yes, Cancel It
                  </Button>
                  <Button
                    variant="text"
                    buttonType="danger"
                    className="mb-xl"
                    onClick={closeCancelModal}
                  >
                    {skipCancelText}
                  </Button>
                </div>
              </Modal>
            </div>
          );
        }}
        getForm={({ handleSubmit }) => {
          return (
            <form className="flex flex-col" onSubmit={handleSubmit}>
              <div className="mb-2xl">
                <RecurringProServiceCard
                  description={preset.description}
                  imageSrc={currentRecurringTask?.image}
                  isHomeownerView={isHomeOwnerView}
                  name={preset.recurringTask!}
                  status={getActiveLeaseUnitRecurringTaskStatus()}
                />
              </div>

              {isActiveByLease && (
                <div className="mb-2xl flex items-center justify-center rounded border border-solid border-green bg-green-translucent-light p-sm">
                  <Text>{STRINGS['disabled-services']}</Text>
                </div>
              )}
              <div className="mb-lg">
                <Text fontWeight="semibold" variant="h3">
                  {STRINGS['frequency-question']}
                </Text>
              </div>
              <div className="mb-2xl">
                <Field
                  name="frequency"
                  component={SelectorFinalFormAdapter}
                  buttonClassName={isMobile ? 'justify-center' : ''}
                  buttons={preset.frequencies
                    .sort((a, b) => (a.value > b.value ? 1 : -1))
                    .map((frequency) => ({
                      label: frequency.displayName,
                      key: frequency.value,
                    }))}
                  type={SELECTOR_TYPES.MEDIUMTEXTBUTTON}
                  withMarginTop={false}
                />
              </div>
              <div className="mb-lg">
                <Text fontWeight="semibold" variant="h3">
                  {STRINGS['extras-question']}
                </Text>
              </div>
              <div className="mb-xl">
                <Field
                  component={InputFinalFormAdapter}
                  name="description"
                  placeholder={STRINGS['unique-needs']}
                  withMarginTop={false}
                />
              </div>
            </form>
          );
        }}
      />
    </FormLayout>
  );
}

const skipCancelText = "NO, DON'T CANCEL IT";
